import type { ReactNode } from "react";
/* 絵は束ねた入口から読まない。後から読むヘッダーの絵まで root の読み込みに残る */
import { UsagiKorobuIcon } from "../base/Icon/UsagiKorobuIcon";
import {
  ArticleCardList,
  DefaultErrorPageLink,
  DestinationList,
  ErrorPageShell,
  ErrorPageSocialLinks,
  type ErrorPageDestination,
  type ErrorPageLinkComponent,
} from "./ErrorPageParts";

export interface NotFoundPageProps {
  /**
   * カスタムLinkコンポーネント（React Router等）
   */
  LinkComponent?: ErrorPageLinkComponent;

  /**
   * 追加のクラス名
   */
  className?: string;

  /** このサイトが何かを一文で。迷い込んだ人が現在地を知るため */
  lead?: string;

  /** 求められた住所に近い、実在するページ */
  suggestions?: readonly ErrorPageDestination[];

  /** 次に行く先 */
  destinations?: readonly ErrorPageDestination[];

  /** 画面の頭に置くサイトのヘッダー。ログイン状態を知るのは呼び出し側 */
  header?: ReactNode;

  /** 研究所の記事。API が落ちていても出せるよう、呼び出し側が静的に持つ */
  articles?: readonly ErrorPageDestination[];

  /** 場面の絵の住所。渡すと、うさぎやかめのアイコンの代わりに絵を出す */
  artSrc?: string;
}

export function NotFoundPage({
  LinkComponent = DefaultErrorPageLink,
  className = "",
  lead,
  suggestions = [],
  destinations = [{ path: "/", title: "ホーム" }],
  header,
  articles = [],
  artSrc,
}: NotFoundPageProps) {
  return (
    <ErrorPageShell
      code={404}
      header={header}
      className={className}
      artSrc={artSrc}
      mascot={<UsagiKorobuIcon className="h-auto w-[132px] sm:w-[180px]" />}
    >
      <div className="flex flex-col gap-3">
        <h1 className="text-xl font-bold leading-snug text-main [word-break:auto-phrase]">
          ページが見つかりません
        </h1>
        <p className="leading-relaxed text-sub text-pretty [word-break:auto-phrase]">
          URLが変わったか、ページが削除された可能性があります。
        </p>
        {lead && <p className="max-sm:text-[0.9375rem] text-sm leading-relaxed text-sub text-pretty [word-break:auto-phrase]">
            {lead}
          </p>}
      </div>
      <DestinationList
        heading="もしかして"
        items={suggestions}
        LinkComponent={LinkComponent}
        listName="suggestion"
      />
      <DestinationList
        heading="次に行く先"
        items={destinations}
        LinkComponent={LinkComponent}
        listName="destination"
      />
      <ArticleCardList
        heading="研究所の記事"
        items={articles}
        LinkComponent={LinkComponent}
        listName="lab-article"
      />
      <ErrorPageSocialLinks />
    </ErrorPageShell>
  );
}
